/**
 * NewIntelSourcePage — /agents/intel-sources/new[?platform=<slug>]
 * Add a source on the FormPage template. Demo pages: /connected-apps (pick an app to connect) and /form-layouts.
 * Step one picks the platform (kept in `?platform=`), step two is the form for its category. Creating a source
 * needs CanManageAgents (createIntelSource).
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { createIntelSourceMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import { Form, FormActions, useZodForm } from '../../../components/form/index.js';
import { FormPage } from '../../../components/page/index.js';
import { Can } from '../../../components/auth/Can.js';
import { Button, Callout } from '../../../components/ui';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { PAGE_HELP } from '../../../help/index.js';
import { NewSourceFields } from './NewSourceFields.js';
import { SOURCE_FORM_DEFAULTS, SOURCE_FORM_SCHEMAS } from './new-source-schemas.js';
import { PLATFORM_GROUPS, findPlatform, type PlatformDef } from './platform-catalog.js';
import { CATEGORY_ICONS, PLATFORM_ICONS } from './platform-icons.js';
import { buildCreatePayloads, getInitialFormData, type FormData } from './source-payloads.js';
import { useIntelSourceInvalidation } from './intel-source-queries.js';

const LIST_PATH = '/agents/intel-sources';

function PlatformIcon({ icon }: { icon: string }) {
  const Icon = PLATFORM_ICONS[icon] ?? Globe;
  return <Icon size={16} className="text-muted" aria-hidden="true" />;
}

/** Step one: every platform, grouped by category. Choosing one sets `?platform=`. */
function PlatformPicker({ onPick }: { onPick: (platform: PlatformDef) => void }) {
  return (
    <div className="space-y-5">
      {PLATFORM_GROUPS.map((group) => {
        const CatIcon = CATEGORY_ICONS[group.category] ?? Globe;
        return (
          <section key={group.category} aria-label={group.label}>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted">
              <CatIcon size={14} aria-hidden="true" />
              {group.label}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {group.platforms.map((p) => (
                <Button
                  key={p.slug}
                  variant="secondary"
                  onClick={() => onPick(p)}
                  className="h-auto items-start justify-start gap-2.5 whitespace-normal p-3 text-left font-normal"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-alt">
                    <PlatformIcon icon={p.icon} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-heading">{p.name}</span>
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted">{p.description}</span>
                  </span>
                </Button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Step two: the category's fields. A pasted list creates one source per line. */
function NewSourceForm({ platform }: { platform: PlatformDef }) {
  const navigate = useNavigate();
  const { notify } = useToast();
  const refresh = useIntelSourceInvalidation();
  const create = useMutation(createIntelSourceMutation());
  const form = useZodForm(SOURCE_FORM_SCHEMAS[platform.category], {
    defaultValues: SOURCE_FORM_DEFAULTS[platform.category],
  });
  const count = buildCreatePayloads(platform, { ...getInitialFormData(), ...form.watch() }).length;

  const submit = async (values: Partial<FormData>) => {
    const payloads = buildCreatePayloads(platform, { ...getInitialFormData(), ...values });
    let added = 0;
    try {
      for (const payload of payloads) {
        await create.mutateAsync({ body: payload });
        added++;
      }
    } finally {
      if (added > 0) refresh();
    }
    notify(added === 1 ? 'Source added.' : `${added} sources added.`);
    navigate(LIST_PATH);
  };

  return (
    <Form form={form} onSubmit={submit} guardUnsaved>
      <div className="flex items-center gap-2 rounded-lg bg-surface-alt p-2">
        <PlatformIcon icon={platform.icon} />
        <span className="text-sm font-medium text-heading">{platform.name}</span>
        <Link
          to="?"
          className={buttonStyles({ variant: 'primary', appearance: 'ghost', size: 'sm', className: 'ml-auto' })}
        >
          Change platform
        </Link>
      </div>
      <NewSourceFields platform={platform} />
      {count > 1 && <Callout tone="accent">{count} sources will be added, one for each entry.</Callout>}
      <FormActions submitLabel={count > 1 ? `Add ${count} sources` : 'Add source'} cancelTo={LIST_PATH} />
    </Form>
  );
}

export const NewIntelSourcePage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const platform = findPlatform(params.get('platform') ?? '');

  return (
    <FormPage
      title="Add source"
      subtitle={platform ? `Add ${platform.name} to your sources.` : 'Choose the platform the source lives on.'}
      helpContent={PAGE_HELP['intel-sources']}
      backTo={LIST_PATH}
      backLabel="Intel sources"
    >
      <Can policy="CanManageAgents">
        {platform ? (
          <NewSourceForm key={platform.slug} platform={platform} />
        ) : (
          <PlatformPicker onPick={(p) => setParams({ platform: p.slug })} />
        )}
      </Can>
    </FormPage>
  );
};
